import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError, api } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi } from "../hierarchy/api";
import { useScopeNames } from "../hierarchy/useScopeNames";
import { RbacApi, type AppUser } from "../rbac/api";
import { SearchableSelect } from "../../shell/SearchableSelect";

interface Person {
  id: string;
  fullName: string;
  position: string | null;
  idNumber: string | null;
  phone: string | null;
  email: string | null;
  notes: string;
  reportsTo: string | null;
  reportsToType: "user" | "person" | null;
  wardId: string;
  constituencyId: string | null;
  createdAt?: string;
  createdBy?: string;
}

interface PersonCreate {
  fullName: string;
  wardId: string;
  position?: string;
  idNumber?: string;
  phone?: string;
  email?: string;
  notes?: string;
  reportsTo?: string;
  reportsToType?: "user" | "person";
}

const PeopleApi = {
  list: (filters?: { wardId?: string }) => {
    const qs = filters?.wardId ? `?wardId=${filters.wardId}` : "";
    return api<{ items: Person[] }>(`/people${qs}`);
  },
  create: (body: PersonCreate) =>
    api<Person>("/people", { method: "POST", body: JSON.stringify(body) }),
  remove: (id: string) => api<void>(`/people/${id}`, { method: "DELETE" }),
};

/**
 * People — non-user contacts captured by ward. Different from `users`:
 * they have no sign-in account, no permissions. Useful for tracking
 * community leaders, informants, and other contacts that operationally
 * matter without granting them platform access.
 */
export function People() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [filterWard, setFilterWard] = useState("");
  const [search, setSearch] = useState("");

  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const { wardName, constituencyName } = useScopeNames();
  const people = useQuery({
    queryKey: ["people", filterWard],
    queryFn: () => PeopleApi.list(filterWard ? { wardId: filterWard } : undefined),
  });

  const create = useMutation({
    mutationFn: PeopleApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["people"] });
      setCreating(false);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: PeopleApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  const items = (people.data?.items ?? [])
    .filter((p) => !search || (p.fullName + " " + (p.notes ?? "")).toLowerCase().includes(search.toLowerCase()));

  const wardsList = scope.data?.wards ?? [];

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>People</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            Community contacts attached to a ward. Different from <strong>Users</strong>: people don't sign in,
            don't hold roles, and can't access the platform — they're tracked for context only.
          </p>
        </div>
        <button onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "+ Add person"}</button>
      </header>

      {error && <div role="alert" className="error">{error}</div>}

      {creating && (
        <CreatePersonForm
          wards={wardsList}
          submitting={create.isPending}
          onSubmit={(v) => { setError(null); create.mutate(v); }}
        />
      )}

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Ward:
          <select value={filterWard} onChange={(e) => setFilterWard(e.target.value)}>
            <option value="">All</option>
            {wardsList.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label>
          Search:
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or notes…" />
        </label>
        <span />
      </div>

      {people.isLoading && <p>Loading…</p>}

      <div className="card-grid">
        {items.map((p) => (
          <div key={p.id} className="list-card">
            <div className="list-card-header">
              <span className="muted" style={{ fontSize: 11 }}>
                {wardName(p.wardId)} · {constituencyName(p.constituencyId)}
              </span>
            </div>
            <div className="list-card-title">{p.fullName}</div>
            {p.position && (
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--accent)" }}>{p.position}</div>
            )}
            <div className="list-card-snippet">{p.notes || "No notes."}</div>
            <div className="list-card-foot">
              <span>{p.phone ?? p.email ?? "—"}</span>
              {/* Delete removed — People records are append-only. */}
            </div>
          </div>
        ))}
        {items.length === 0 && !people.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            No people yet. {wardsList.length === 0 ? "Build the hierarchy first." : "Add one above."}
          </div>
        )}
      </div>
    </div>
  );
}

function CreatePersonForm({
  wards, submitting, onSubmit,
}: {
  wards: { id: string; name: string }[];
  submitting: boolean;
  onSubmit: (v: PersonCreate) => void;
}) {
  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const peopleAll = useQuery({ queryKey: ["people"], queryFn: () => api<{ items: Person[] }>("/people") });

  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [wardId, setWardId] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [reportsToValue, setReportsToValue] = useState("");

  // reportsTo combines users + other people in one searchable list. We
  // encode the type into the option value so the server can split them
  // back out: "user:UID" or "person:PID".
  const reportsToOptions = [
    ...((users.data?.items ?? []) as AppUser[]).map((u) => ({
      value: `user:${u.id}`,
      label: u.fullName || u.email,
      hint: `User · ${u.email}`,
    })),
    ...((peopleAll.data?.items ?? []) as Person[]).map((p) => ({
      value: `person:${p.id}`,
      label: p.fullName,
      hint: `Person · ${p.position ?? "no title"}`,
    })),
  ];

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!fullName.trim() || !wardId) return;
    let reportsTo: string | undefined;
    let reportsToType: "user" | "person" | undefined;
    if (reportsToValue.startsWith("user:")) {
      reportsToType = "user";
      reportsTo = reportsToValue.slice(5);
    } else if (reportsToValue.startsWith("person:")) {
      reportsToType = "person";
      reportsTo = reportsToValue.slice(7);
    }
    onSubmit({
      fullName: fullName.trim(),
      position: position.trim() || undefined,
      wardId,
      idNumber: idNumber || undefined,
      phone: phone || undefined,
      email: email || undefined,
      notes,
      reportsTo,
      reportsToType,
    });
    setFullName(""); setPosition(""); setIdNumber(""); setPhone(""); setEmail(""); setNotes(""); setReportsToValue("");
  }

  return (
    <form className="create-role" onSubmit={submit} style={{ marginTop: 12 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Capture a community contact. Personal info (name, ID, phone, email, notes) is encrypted at rest.
      </div>
      <div className="row-3">
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" required />
        <input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Position (e.g. Headman, Pastor, Teacher)" />
        <SearchableSelect
          value={wardId}
          onChange={setWardId}
          options={wards.map((w) => ({ value: w.id, label: w.name }))}
          placeholder="— ward —"
          required
        />
      </div>
      <div className="row-3" style={{ marginTop: 8 }}>
        <input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="ID number (optional)" spellCheck={false} />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" spellCheck={false} />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" spellCheck={false} />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
          Reports to (optional) — places this person in the Organogram
        </label>
        <SearchableSelect
          value={reportsToValue}
          onChange={setReportsToValue}
          options={reportsToOptions}
          placeholder="— pick a senior (user or person) —"
        />
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes — role in community, why captured, anything else"
        rows={3}
        style={{ width: "100%", marginTop: 8 }}
      />
      <div style={{ marginTop: 12 }}>
        <button type="submit" disabled={submitting || !fullName.trim() || !wardId}>
          {submitting ? "Adding…" : "Add person"}
        </button>
      </div>
    </form>
  );
}
